import type React from 'react';
import { ReactNode, useEffect, useId, useRef } from 'react';
import { Modal as FlowbiteModal } from 'flowbite-react';
import { FlatCards } from './PanelCard';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Mono kicker above the title (design drawer `.dh .eyebrow`). */
  eyebrow?: ReactNode;
  /** 13px muted line under the title (design drawer `.dh`). */
  subtitle?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * 'right' docks a square panel full-height against the right edge (design `.drawer`): 440px wide
   * unless an explicit `size` is passed, which then sets its max width as for centre modals.
   */
  side?: 'center' | 'right';
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  /** Element to focus on open; defaults to the first focusable control in the body (not the header close). */
  initialFocus?: () => HTMLElement | null;
  children: ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  // xl is for side-by-side content (e.g. four exam forms) that needs the full
  // width of a wide screen.
  xl: 'max-w-6xl',
};

const FLOWBITE_SIZES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'sm',
  md: 'lg',
  lg: '2xl',
  xl: '6xl',
};

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// The topmost open dialog (modals can stack, e.g. a confirm over a form).
const topDialog = (): HTMLElement | null => {
  const all = document.querySelectorAll<HTMLElement>('[role="dialog"]');
  return all.length ? all[all.length - 1] : null;
};

// Open modals in open order; Escape must only close the topmost one.
const openStack: symbol[] = [];

// A control is skipped when it (or an ancestor inside the root) is not rendered or is hidden from assistive tech.
const isHiddenControl = (el: HTMLElement, root: HTMLElement) => {
  if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return true;
  if (window.getComputedStyle(el).visibility === 'hidden') return true;
  for (let n: HTMLElement | null = el; n && n !== root.parentElement; n = n.parentElement) {
    if (window.getComputedStyle(n).display === 'none') return true;
  }
  return false;
};

const focusablesIn = (root: HTMLElement) =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !isHiddenControl(el, root));

export default function Modal({
  isOpen,
  onClose,
  title,
  eyebrow,
  subtitle,
  size,
  side = 'center',
  footer,
  closeOnBackdrop = true,
  closeOnEsc = true,
  initialFocus,
  children,
}: ModalProps) {
  const eyebrowId = useId();
  const subtitleId = useId();
  const initialFocusFn = useRef(initialFocus);
  initialFocusFn.current = initialFocus;
  // Lazily resolved by the dialog library when it moves focus in: the override, else the
  // first control in the body, else the first control anywhere in the dialog.
  const initialFocusRef = useRef({
    get current(): HTMLElement | null {
      const custom = initialFocusFn.current?.();
      if (custom) return custom;
      const dialog = topDialog();
      if (!dialog) return null;
      const body = dialog.querySelector<HTMLElement>('[data-modal-body]');
      return (body && focusablesIn(body)[0]) || focusablesIn(dialog)[0] || null;
    },
  } as { current: HTMLElement | null });
  // The element that had focus when the dialog opened; focus goes back to it on close.
  // Captured once on the closed -> open transition (render phase, before any focus move), so
  // StrictMode's double render/effect cannot overwrite it with an element inside the dialog.
  const triggerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const openRef = useRef(isOpen);
  openRef.current = isOpen;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  if (isOpen && !wasOpenRef.current) {
    wasOpenRef.current = true;
    triggerRef.current = typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null;
  } else if (!isOpen) {
    wasOpenRef.current = false;
  }

  // Focus management: into the dialog on open, Tab cycles inside it, back to the trigger on close.
  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => {
      const dialog = topDialog();
      if (!dialog || dialog.contains(document.activeElement)) return;
      const first = initialFocusRef.current.current;
      if (first) first.focus();
      else { dialog.setAttribute('tabindex', '-1'); dialog.focus(); }
    }, 0);
    function onTab(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const dialog = topDialog();
      if (!dialog) return;
      const items = focusablesIn(dialog);
      if (items.length === 0) { e.preventDefault(); dialog.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (active === first || active === dialog)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onTab);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onTab);
      const restore = () => {
        const back = triggerRef.current;
        triggerRef.current = null;
        if (back && back.isConnected) back.focus();
      };
      if (!openRef.current) { restore(); return; }
      // Still open: either an unmount while open (restore) or StrictMode's simulated
      // unmount (the remount sets mountedRef again before this microtask runs).
      queueMicrotask(() => { if (!mountedRef.current) restore(); });
    };
  }, [isOpen]);

  // Body scroll lock while open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // ESC handler (flowbite has its own dismiss, but we wire it explicitly so
  // closeOnEsc=false is respected).
  // Stack position is claimed once per open (not per onClose identity), so a parent
  // re-rendering with a fresh onClose cannot jump above a modal opened later.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const stackIdRef = useRef<symbol>(Symbol('modal'));
  useEffect(() => {
    if (!isOpen) return;
    const id = stackIdRef.current;
    openStack.push(id);
    return () => {
      const i = openStack.indexOf(id);
      if (i >= 0) openStack.splice(i, 1);
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    function onKey(e: KeyboardEvent) {
      // Only the topmost open modal reacts; ones beneath it stay open.
      if (e.key === 'Escape' && openStack[openStack.length - 1] === stackIdRef.current) onCloseRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEsc]);

  if (!isOpen) return null;
  const describedBy = [title !== undefined && eyebrow ? eyebrowId : null, title !== undefined && subtitle ? subtitleId : null].filter(Boolean).join(' ') || undefined;

  return (
    <FlowbiteModal
      show={isOpen}
      onClose={onClose}
      // Flowbite's own dismiss would also close on Escape for every stacked modal at once;
      // Escape is handled above (topmost only) and backdrop presses here.
      dismissible={false}
      onMouseDown={(e: React.MouseEvent<HTMLElement>) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
      // Initial focus is ours alone (the open effect above, which never steals focus from a control
      // already inside the dialog). Letting the dialog's focus manager also apply it re-runs on
      // later renders and pulls focus back to the first field mid-typing.
      initialFocus={-1}
      // A drawer's wrapper is never capped below the panel's own width.
      size={(side === 'right' ? '7xl' : FLOWBITE_SIZES[size ?? 'md']) as 'sm' | 'lg' | '2xl' | '6xl' | '7xl'}
      aria-describedby={describedBy}
      position={side === 'right' ? 'center-right' : undefined}
      theme={{
        // Design `.scrim`: navy at 38%.
        root: { show: { on: 'flex bg-navy/[.38]', off: 'hidden' } },
        content: side === 'right'
          ? {
            base: 'relative h-full w-full',
            // Design `.drawer`: square corners, 440px, a left-cast shadow; panel base text (the portal sits outside the layout root).
            inner: `relative ml-auto flex h-screen max-h-screen ${size ? `w-full ${SIZE_CLASSES[size]}` : 'w-[440px] max-w-full'} flex-col bg-surface text-sm leading-[1.45] text-ink shadow-drawer`,
          }
          : { inner: `relative mx-auto flex max-h-[90dvh] flex-col rounded-[20px] bg-surface text-sm leading-[1.45] text-ink shadow-card ${SIZE_CLASSES[size ?? 'md']}` },
        // min-h-0 lets the body shrink and scroll inside the capped panel; header and footer stay visible.
        body: { base: 'min-h-0 flex-1 overflow-auto px-6 py-5' },
        header: {
          base: 'flex items-start justify-between gap-3 border-b border-hairline px-6 pb-[18px] pt-6',
          title: 'min-w-0 font-display text-[22px] font-bold leading-tight tracking-[-0.01em] text-ink',
          // Design `.ab`: 34px bordered icon button.
          close: {
            base: 'ml-auto inline-grid h-[34px] w-[34px] flex-none place-items-center rounded-[10px] border border-line-strong bg-surface p-0 text-muted-2 hover:bg-note hover:text-ink',
            icon: 'h-4 w-4',
          },
        },
      }}>
      {title !== undefined && (
        // The eyebrow and subtitle sit inside the heading box for layout but are aria-hidden, so the
        // dialog and heading are named by the title alone; the dialog's description references them.
        <FlowbiteModal.Header>
          {eyebrow && <span id={eyebrowId} aria-hidden="true" className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">{eyebrow}</span>}
          {title}
          {subtitle && <span id={subtitleId} aria-hidden="true" className="mt-1 block font-sans text-[13px] font-normal tracking-normal text-muted">{subtitle}</span>}
        </FlowbiteModal.Header>
      )}
      {/* Cards inside a dialog are never flattened by a `flatChildren` card the dialog was opened from. */}
      <FlowbiteModal.Body><FlatCards.Provider value={false}><div data-modal-body>{children}</div></FlatCards.Provider></FlowbiteModal.Body>
      {footer && (
        <div className="flex flex-none items-center justify-end gap-2.5 border-t border-hairline px-6 py-4">
          {footer}
        </div>
      )}
    </FlowbiteModal>
  );
}
